/*
 * WHERE TO SEND A READER WHOSE ARRIVAL FAILED.
 *
 * The library is fetched from two hosts before a word is drawn — the manifest from the API, the
 * bundle from the edge — and when either does not answer, the app shows one sentence: "The library
 * could not be reached." and nothing else. No retry, no way on, no way back.
 *
 * That sentence is shown to a reader who has just followed one of the 52,463 links the static pages
 * carry. They did not come to the front door and bounce; they came for Abraham, from a page that was
 * already open in front of them. Leaving them on a dead end is losing the one reader who had already
 * chosen.
 *
 * AND THE OTHER HALF OF THIS LIBRARY NEEDS NONE OF WHAT JUST FAILED. The 2,688 static pages are
 * HTML: no manifest, no bundle, no API, no JavaScript at all. Whatever broke, they are still there.
 *
 * So the error offers two things a reader can actually do — try again, and read it as a page — and
 * this decides where the second one goes. NOTHING HERE SPELLS A PAGE PATH. `/n/abraham` is spelled
 * by `pipeline/lib/node-pages.ts`, in a codebase the app cannot import, and a second copy of that
 * format here is the drift this repository has paid for repeatedly. The referrer is the page the
 * reader was ON, which is better than anything this could reconstruct, and `/n/` is a family root
 * rather than a path built per item.
 */

/** The figures hub — a family root, not a path this file assembles from an id. */
export const PAGES_FALLBACK = '/n/';

/**
 * The page to offer, given where the reader came from.
 *
 * The referrer wins when it is a page on this same origin: it is the page they were reading, and it
 * is where they will find what they came for. Anything else — a search result, a chat app, a bare
 * address typed in, a referrer stripped by policy — falls back to the hub.
 *
 * THE APP ITSELF IS NOT AN ANSWER, and that is the case worth writing down: a reader who reloaded a
 * failing `?node=` link has the app as their referrer, and offering it back would be a button that
 * returns them to the screen they are trying to leave.
 */
export function pagesFallback(referrer: string | null | undefined, origin: string): string {
  if (referrer == null || referrer === '') return PAGES_FALLBACK;
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return PAGES_FALLBACK;
  }
  if (url.origin !== origin) return PAGES_FALLBACK;
  /* The app is served at the root; a referrer with no path beyond `/` is the app, not a page. */
  if (url.pathname === '/' || url.pathname === '') return PAGES_FALLBACK;
  return `${url.pathname}${url.search}`;
}
